/*
 * Copyright (c) 2025 Amazon.com, Inc. or its affiliates. All rights reserved.
 *
 * PROPRIETARY/CONFIDENTIAL. USE IS SUBJECT TO LICENSE TERMS.
 */

import {KatGrid} from '@amzn/katal-react';
import {PhotoInputItem, PhotoPreviewItem} from './PhotoGridItem';
import {MAXIMUM_PHOTO_COUNT} from '../photoForm/validation';
import {usePhotoFormController} from '../photoForm/PhotoFormController';

const GRID_COLUMN_COUNT = 2;

export type PhotoPickerProps = Pick<KatGrid.Props, 'className'>;

/** Displays a photo picker component. Only JPEGs and PNGs are supported. */
export const PhotoPicker = ({className}: PhotoPickerProps) => {
  const {
    fields,
    formState: {isSubmitting},
    remove,
    onChange,
  } = usePhotoFormController();
  const placeholderItemCount = Math.max(MAXIMUM_PHOTO_COUNT - fields.length, 0);

  return (
    <KatGrid
      className={className}
      density="high"
      colsXxl={GRID_COLUMN_COUNT}
      colsXl={GRID_COLUMN_COUNT}
      colsL={GRID_COLUMN_COUNT}
      colsM={GRID_COLUMN_COUNT}
      colsS={GRID_COLUMN_COUNT}>
      {fields.map(({inputFile, id}, i) => (
        <PhotoPreviewItem
          key={id}
          file={inputFile}
          onRemove={() => remove(i)}
        />
      ))}
      {Array.from({length: placeholderItemCount}).map((_, i) => (
        <PhotoInputItem
          key={`PhotoInputItem-${i}`}
          disabled={isSubmitting}
          onChange={onChange}
        />
      ))}
    </KatGrid>
  );
};
